import React, { useEffect, useState } from 'react';
import { Typography, Box, Paper, List, ListItem, ListItemText, CircularProgress } from '@mui/material'; 
import { useSelector } from 'react-redux';
import { collection, getDocs, query, where } from 'firebase/firestore'; 
import { db } from '../firebase/firebase';
import { selectUserID } from '../redux/slice/authSlice';
import { toast } from 'react-toastify'; 

const UserBehaviorAnalysis = () => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState(null); 
  const currentUserID = useSelector(selectUserID);

  useEffect(() => {
    const fetchAndAnalyzeTasks = async () => {
      if (!currentUserID) {
        setInsights(null); 
        setLoading(false);
        setError(null); 
        return; 
      }

      setLoading(true); 
      setError(null); 
      try {
        const tasksCollectionRef = collection(db, 'tasks');
       
        const q = query(
          tasksCollectionRef,
          where("userId", "==", currentUserID) 
        );
        const querySnapshot = await getDocs(q);
        const allUserTasks = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const userCompletedTasks = allUserTasks.filter(task =>
          task.completed
        );

        if (userCompletedTasks.length === 0) {
          setInsights({ message: "No completed tasks to analyze yet." });
          setLoading(false);
          return;
        }

        // --- Perform your analysis here ---
        let totalTimeSpent = 0;
        const priorityCounts = {};
        const categoryCounts = {};
        let longestTask = null;

        userCompletedTasks.forEach(task => {
          if (task.timeSpent && typeof task.timeSpent === 'number') {
            totalTimeSpent += task.timeSpent;
            if (!longestTask || task.timeSpent > longestTask.timeSpent) {
                longestTask = task;
            }
          }
          if (task.priority) {
              priorityCounts[task.priority] = (priorityCounts[task.priority] || 0) + 1;
          }
          if (task.category) {
              categoryCounts[task.category] = (categoryCounts[task.category] || 0) + 1;
          }
        });

        const avgCompletionTime = userCompletedTasks.length > 0
          ? (totalTimeSpent / userCompletedTasks.length).toFixed(1)
          : 0;

        setInsights({
          message: `${userCompletedTasks.length} tasks completed.`,
          averageTime: `${avgCompletionTime} hours`,
          mostCommonPriority: Object.keys(priorityCounts).length > 0 ? Object.keys(priorityCounts).reduce((a, b) => priorityCounts[a] > priorityCounts[b] ? a : b, 'none') : 'N/A',
          mostCommonCategory: Object.keys(categoryCounts).length > 0 ? Object.keys(categoryCounts).reduce((a, b) => categoryCounts[a] > categoryCounts[b] ? a : b, 'none') : 'N/A',
          longestTask: longestTask ? longestTask.title + ` (${longestTask.timeSpent} hrs)` : 'N/A'
        });

      } catch (err) {
        console.error("Error analyzing user behavior:", err);
        setError("Failed to load insights. Please try again."); 
        toast.error("Failed to load task insights."); 
      } finally {
        setLoading(false); 
      }
    };

    fetchAndAnalyzeTasks();
  }, [currentUserID]); 

  // Render logic
  return (
    <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
      <Typography variant="h5" gutterBottom>Your Task Behavior Insights</Typography>
      {!currentUserID && (
        <Typography color="textSecondary">Sign in to view personalized behavior insights.</Typography>
      )}
      {currentUserID && loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}
      {currentUserID && error && (
        <Typography color="error">{error}</Typography>
      )}
      {currentUserID && !loading && !error && insights && (
        <List>
          {insights.message && <ListItem><ListItemText primary={insights.message} /></ListItem>}
          {insights.averageTime && <ListItem><ListItemText primary={`Average Task Time: ${insights.averageTime}`} /></ListItem>}
          {insights.mostCommonPriority && <ListItem><ListItemText primary={`Most Common Priority: ${insights.mostCommonPriority}`} /></ListItem>}
          {insights.mostCommonCategory && <ListItem><ListItemText primary={`Most Common Category: ${insights.mostCommonCategory}`} /></ListItem>}
          {insights.longestTask && <ListItem><ListItemText primary={`Longest Completed Task: ${insights.longestTask}`} /></ListItem>}
        </List>
      )}
      {currentUserID && !loading && !error && !insights && (
          <Typography>No analysis data available yet. Create and complete some tasks!</Typography>
      )}
    </Paper>
  );
};

export default UserBehaviorAnalysis;
